import React, { useEffect, useRef } from 'react';

import { Spinner } from '@/components/Spinner';
import { entitify } from '@/lib/util';

import styles from './PanelGlyphs.module.scss';

export const hexToDecimal = (charDecimal) => {
  return parseInt(charDecimal, 16);
};

const htmlEntity = (string) => {
  return `&#x${string};`;
};

const htmlCode = (charDecimal) => {
  return `&#${parseInt(charDecimal, 16)};`;
};

const getCssVariableValue = (variable) =>
  getComputedStyle(document.documentElement).getPropertyValue(variable);

// Opentype.js function that fixes the canvas scaling on mobile devices https://github.com/opentypejs/opentype.js/blob/1c0c01982f9855cb5ceff64c9465af1be5cf8876/glyph-inspector.html#L76-L89
const enableHighDPICanvas = (canvas, pixelRatio) => {
  if (!canvas || pixelRatio === 1) return;

  const canvasClass = canvas.classList;

  if (canvasClass.length === 1) {
    const oldWidth = canvas?.width;
    const oldHeight = canvas?.height;

    canvas.width = oldWidth * pixelRatio;
    canvas.height = oldHeight * pixelRatio;
    canvas.getContext('2d').scale(pixelRatio, pixelRatio);
    canvas.classList.add('high-dpi'); // Add this class flag so we'll just run this function once
  }
};

const dashline = (ctx, ctxWidth, yunits) => {
  ctx.strokeStyle = getCssVariableValue('--grey-primary');

  ctx.beginPath();
  ctx.setLineDash([4, 4]);
  ctx.moveTo(0, yunits);
  ctx.lineTo(ctxWidth, yunits);
  ctx.stroke();
};

const hline =
  (ctx, ctxWidth) =>
  (yunits, { text = '', top = true, dashed = false }) => {
    const textYunits = top ? yunits - 5 : yunits + 20;

    ctx.fillText(text, 0, textYunits);

    return dashed ? dashline(ctx, ctxWidth, yunits) : ctx.fillRect(0, yunits, ctxWidth, 1.5);
  };

// For drawing the Glyph on canvas https://github.com/opentypejs/opentype.js/blob/1c0c01982f9855cb5ceff64c9465af1be5cf8876/glyph-inspector.html#L194-L237
const drawGlyphPath = (ctx, path) => {
  let i, cmd, x1, y1, x2, y2;

  ctx.beginPath();

  for (i = 0; i < path.commands.length; i += 1) {
    cmd = path.commands[i];
    if (cmd.type === 'M') {
      ctx.moveTo(cmd.x, cmd.y);
    } else if (cmd.type === 'L') {
      ctx.lineTo(cmd.x, cmd.y);
      x1 = x2;
      y1 = y2;
    } else if (cmd.type === 'C') {
      ctx.bezierCurveTo(cmd.x1, cmd.y1, cmd.x2, cmd.y2, cmd.x, cmd.y);
      x1 = cmd.x2;
      y1 = cmd.y2;
    } else if (cmd.type === 'Q') {
      ctx.quadraticCurveTo(cmd.x1, cmd.y1, cmd.x, cmd.y);
      x1 = cmd.x1;
      y1 = cmd.y1;
    } else if (cmd.type === 'Z') {
      ctx.closePath();
    }
    x2 = cmd.x;
    y2 = cmd.y;
  }

  if (path.fill) {
    ctx.fillStyle = path.fillStyle || getCssVariableValue('--white');
    ctx.fill();
  }

  if (path.stroke) {
    ctx.strokeStyle = path.strokeStyle || getCssVariableValue('--white');
    ctx.lineWidth = path.strokeWidth;
    ctx.stroke();
  }
};

const GlyphModalContent = ({ glyphData }) => {
  const canvasRef = useRef(null);
  const canvasBgRef = useRef(null);

  const PIXEL_RATIO = (typeof window !== 'undefined' && window.devicePixelRatio) || 1;
  const CANVAS_HEIGHT = 450;
  const CANVAS_WIDTH = 650;
  const GLYPH_MARGIN = 10;

  const { script, subCategory, char } = glyphData || {};

  const renderGlyph = (glyphData) => {
    const canvas = canvasRef.current;
    const canvasCtx = canvas?.getContext('2d');
    const width = canvas.width / PIXEL_RATIO;
    const height = canvas.height / PIXEL_RATIO;

    canvasCtx.clearRect(0, 0, width, height);

    canvasCtx.textAlign = 'center';
    canvasCtx.textBaseline = 'middle';

    const { path } = glyphData;

    drawGlyphPath(canvasCtx, path);
  };

  const renderCanvas = (glyphData) => {
    const canvas = canvasBgRef.current; //canvasBg
    const canvasCtx = canvas?.getContext('2d'); //glyphBgCtx

    const canvasPixelRatio = PIXEL_RATIO;
    const canvasMargin = GLYPH_MARGIN;

    const canvasWidth = canvas.width;
    const canvasHeight = canvas.height;

    const canvasWidthComputed = canvasWidth / canvasPixelRatio; //w
    const canvasHeightComputed = canvasHeight / canvasPixelRatio; //h

    const glyphCanvasWidthComputed = canvasWidthComputed - canvasMargin * 2; //glyphW
    const glyphCanvasHeightComputed = canvasHeightComputed - canvasMargin * 2; //glyphH

    const glyphYMax = Math.max(glyphData.tables.head.yMax, glyphData.tables.os2.sTypoAscender);
    const glyphYMin = Math.min(glyphData.tables.head.yMin, glyphData.tables.os2.sTypoDescender);

    const glyphMaxWidth = glyphData.advanceWidth; //maxWidth
    const glyphMaxHeight = glyphYMax - glyphYMin; //maxHeight

    canvasCtx.clearRect(0, 0, canvasWidthComputed, canvasHeightComputed);
    canvasCtx.fillStyle = getCssVariableValue('--grey-primary');
    canvasCtx.font = `${14 * (canvasWidth / canvasHeight)}px Basis-Grotesque-Mono-Pro`;

    const scale = Math.min(
      glyphCanvasWidthComputed / glyphMaxWidth,
      glyphCanvasHeightComputed / glyphMaxHeight
    );

    const baseline = canvasMargin + glyphYMax * scale;

    const descenderValue =
      glyphData.controlBoundingBox.y1 < 0
        ? glyphData.controlBoundingBox.y1
        : glyphData.tables.os2.sTypoDescender;
    const descender = baseline - descenderValue * scale;
    const xheight = baseline - glyphData.tables.os2.sxHeight * scale;
    const capheight = baseline - glyphData.tables.os2.sCapHeight * scale;

    const drawHLine = hline(canvasCtx, canvasWidthComputed);

    drawHLine(capheight, { text: '1', top: true, dashed: false });
    drawHLine(xheight, { text: '2', top: true, dashed: true });
    drawHLine(baseline, { text: '3', top: true, dashed: false });
    drawHLine(descender, { text: '4', top: true, dashed: true });

    renderGlyph(glyphData);
  };

  useEffect(() => {
    enableHighDPICanvas(canvasRef?.current, PIXEL_RATIO);
    enableHighDPICanvas(canvasBgRef?.current, PIXEL_RATIO);

    if (glyphData) renderCanvas(glyphData);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [glyphData]);

  return (
    <div className={styles.modal}>
      {!glyphData && <Spinner theme="white" contextual />}
      <div className={styles.modal__Left}>
        <h5>
          <>
            {script ? `${script} ` : ''}
            {subCategory ? `${subCategory}` : ''}
            &nbsp;{entitify(char)}
          </>
        </h5>
        <ul className={styles.modal__LeftEntities}>
          <li>
            <div>Unicode DECIMAL</div>
            {hexToDecimal(char)}
          </li>
          <li>
            <div>Unicode HEX</div>
            {char}
          </li>
          <li>
            <div>HTML Entity (Hex)</div>
            {htmlEntity(char)}
          </li>
          <li>
            <div>HTML Code</div>
            {htmlCode(char)}
          </li>
          <li>
            <div>CSS Code</div>
            {`\\${char}`}
          </li>
        </ul>
        <ul className={styles.modal__LeftEntities}>
          <li>
            <div>1</div>
            Cap Height
          </li>
          <li>
            <div>2</div>
            X-Height
          </li>
          <li>
            <div>3</div>
            Baseline
          </li>
          <li>
            <div>4</div>
            Descender
          </li>
        </ul>
      </div>
      <div className={styles.modal__Right}>
        <h5>{`U+${char}`}</h5>
        <div className={styles.canvasContainer}>
          <canvas
            ref={canvasRef}
            id="glyph"
            className={styles.modalGlyph}
            width={CANVAS_WIDTH}
            height={CANVAS_HEIGHT}
          />
          <canvas
            ref={canvasBgRef}
            id="glyphBg"
            className={styles.modalGlyphBg}
            width={CANVAS_WIDTH}
            height={CANVAS_HEIGHT}
          />
        </div>
      </div>
    </div>
  );
};

export default GlyphModalContent;
